import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Target, Heart, Shield, Play, Loader2, CheckCircle2, XCircle,
  ArrowRight, Sparkles, ChevronDown, ChevronUp, Clock,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Chain {
  id: string;
  name: string;
  description: string;
  icon: string;
  steps_count: number;
}

interface ChainStep {
  step: number;
  agent_id: string;
  agent_name: string;
  mission: string;
  output: string;
  status: "completed" | "failed" | "pending";
  started_at: string;
  completed_at: string;
}

interface ChainRun {
  id: string;
  chain_id: string;
  chain_name: string;
  steps: ChainStep[];
  total_steps: number;
  completed_steps: number;
  created_at: string;
  recap_email_sent?: boolean;
}

const ICON_MAP: Record<string, React.ElementType> = {
  target: Target,
  heart: Heart,
  shield: Shield,
};

const CHAIN_STYLE: Record<string, { grad: string; bg: string; border: string; text: string }> = {
  acquisition: { grad: "from-emerald-500/20 via-emerald-500/5 to-transparent", bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400" },
  retention: { grad: "from-pink-500/20 via-pink-500/5 to-transparent", bg: "bg-pink-500/10", border: "border-pink-500/30", text: "text-pink-400" },
  compliance: { grad: "from-blue-500/20 via-blue-500/5 to-transparent", bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400" },
};

export function AgentChains() {
  const [chains, setChains] = useState<Chain[]>([]);
  const [history, setHistory] = useState<ChainRun[]>([]);
  const [runningChain, setRunningChain] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<ChainRun | null>(null);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadChains();
    loadHistory();
  }, []);

  const loadChains = async () => {
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/chains`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setChains(d.chains || []);
      }
    } catch {/* empty */}
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/chain/history`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setHistory(d.runs || []);
      }
    } catch {/* empty */}
  };

  const runChain = async (chainId: string) => {
    setRunningChain(chainId);
    setLastRun(null);
    setExpandedStep(null);
    try {
      toast.loading(`Exécution de la chaîne "${chainId}"...`, { id: "chain-run" });
      const res = await fetch(`${API_URL}/api/nanocorp/chain/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ chain_id: chainId }),
      });
      if (res.ok) {
        const d = await res.json();
        setLastRun(d);
        const emailHint = d.recap_email_sent ? " — récap envoyé par email 📧" : "";
        toast.success(`Chaîne exécutée — ${d.completed_steps}/${d.total_steps} étapes${emailHint}`, { id: "chain-run" });
        setExpandedStep(d.steps.length); // Expand last step
        loadHistory();
      } else {
        toast.error("Erreur lors de l'exécution", { id: "chain-run" });
      }
    } catch {
      toast.error("Erreur réseau", { id: "chain-run" });
    } finally {
      setRunningChain(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-chains">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Chaînes d'agents</h3>
          </div>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Plusieurs agents NanoCorp travaillent en séquence : la sortie de l'un devient l'entrée du suivant.
            C'est Grafton qui bosse tout seul, comme une vraie équipe.
          </p>
        </div>
        {history.length > 0 && (
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-chain-history">
            <Clock className="h-3 w-3" /> Historique ({history.length})
            {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        )}
      </div>

      {/* Chains grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {chains.map((c) => {
          const Icon = ICON_MAP[c.icon] || Sparkles;
          const style = CHAIN_STYLE[c.id] || { grad: "from-primary/20 via-primary/5 to-transparent", bg: "bg-primary/10", border: "border-primary/30", text: "text-primary" };
          const isRunning = runningChain === c.id;
          return (
            <Card key={c.id} className={`relative overflow-hidden border-border/50 hover:${style.border} transition-all`} data-testid={`chain-card-${c.id}`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${style.grad} pointer-events-none`} />
              <CardContent className="relative p-5 space-y-4">
                <div className={`inline-flex h-10 w-10 rounded-xl ${style.bg} border ${style.border} items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${style.text}`} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold">{c.name}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed min-h-[36px]">{c.description}</p>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border/30">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{c.steps_count} étapes</span>
                  <Button
                    size="sm"
                    className="h-8 text-xs gap-1.5"
                    onClick={() => runChain(c.id)}
                    disabled={!!runningChain}
                    data-testid={`run-chain-${c.id}`}
                  >
                    {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                    {isRunning ? "En cours..." : "Lancer"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Last run results */}
      {lastRun && (
        <Card className="border-primary/30 border-2" data-testid="last-chain-run">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  Résultat : {lastRun.chain_name}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {lastRun.completed_steps}/{lastRun.total_steps} étapes terminées
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setLastRun(null)} className="text-xs">Fermer</Button>
            </div>

            {/* Steps timeline */}
            <div className="space-y-3">
              {lastRun.steps.map((step) => {
                const isExpanded = expandedStep === step.step;
                const isFailed = step.status === "failed";
                return (
                  <div key={step.step} className={`rounded-lg border ${isFailed ? "border-red-500/30 bg-red-500/5" : "border-border/30 bg-muted/10"}`}>
                    <button
                      className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/20 transition-colors"
                      onClick={() => setExpandedStep(isExpanded ? null : step.step)}
                      data-testid={`chain-step-${step.step}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isFailed ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                          {step.step}
                        </span>
                        <div>
                          <p className="text-sm font-semibold">{step.agent_name}</p>
                          <p className="text-[10px] text-muted-foreground line-clamp-1">{step.mission}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isFailed ? <XCircle className="h-4 w-4 text-red-400" /> : <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 border-t border-border/30">
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed mt-3">
                          {step.output}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History panel */}
      {showHistory && (
        <Card className="border-border/50" data-testid="chain-history-panel">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-border/30 flex items-center gap-2 bg-muted/10">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <h4 className="text-xs font-semibold uppercase tracking-wider">Historique des exécutions</h4>
            </div>
            <div className="divide-y divide-border/30 max-h-[320px] overflow-y-auto">
              {history.map((run) => (
                <button
                  key={run.id}
                  onClick={() => { setLastRun(run); setExpandedStep(run.steps.length); }}
                  className="w-full px-5 py-3 hover:bg-muted/20 flex items-center justify-between text-left transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium">{run.chain_name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(run.created_at).toLocaleString("fr-FR")} — {run.completed_steps}/{run.total_steps} étapes
                    </p>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
